import { useId, useState } from "react";
import type { Dimension, FilterField } from "@metrickle/schema";
import { useBreakdown, useGoals, usePropertyKeys, useSummary, useTimeseries } from "../lib/api";
import { useStatsParams } from "../lib/params";
import { A11Y_LABEL, countryName, flag, fmtBucket, fmtBucketHeader, fmtMoney, fmtNumber, fmtPercent, DIMENSION_LABEL } from "../lib/format";
import { AppHeader, useAppId } from "../components/app-header";
import { StatsToolbar } from "../components/layout";
import { SummaryTiles, metricOf, type MetricKey } from "../components/stats";
import { ChartLegend, LineChart } from "../components/charts/LineChart";
import { LatestReleaseStrip, ReleaseList, releaseMarkers, useReleases } from "../components/releases";
import { BarList } from "../components/charts/small";
import { Card, CardHeader, Empty, ErrorState, InlineCode, Segmented, SelectField, Skeleton, TabBar } from "../components/ui";

export function Overview() {
  const appId = useAppId();
  const [metric, setMetric] = useState<MetricKey>("visitors");
  const [compare, setCompare] = useState("on");
  const summary = useSummary(appId);
  const ts = useTimeseries(appId, compare === "on");
  const buckets = ts.data?.current.map((p) => p.t) ?? [];
  // End of the last bucket: stable across renders, unlike Date.now().
  const end = buckets.length ? buckets[buckets.length - 1]! + (buckets.length > 1 ? buckets[1]! - buckets[0]! : 86_400_000) : undefined;
  const releases = useReleases(appId, buckets[0], end);

  return (
    <>
      <AppHeader />
      <StatsToolbar appId={appId}>
        <Segmented aria-label="Compare with previous period" items={[{ value: "on", label: "Compare" }, { value: "off", label: "No compare" }]} value={compare} onChange={setCompare} />
      </StatsToolbar>
      <LatestReleaseStrip appId={appId} />
      <div className="space-y-4">
        <Card data-tour="overview-summary">
          <SummaryTiles
            current={summary.data?.current}
            previous={summary.data?.previous}
            loading={summary.isPending}
            selected={metric}
            onSelect={setMetric}
            compare={compare === "on"}
            interval={ts.data?.interval}
          >
            {summary.isError || ts.isError ? (
              <ErrorState error={summary.error ?? ts.error} />
            ) : ts.data ? (
              <>
                <LineChart
                  labels={ts.data.current.map((p) => fmtBucket(p.t, ts.data.interval))}
                  tooltipLabels={ts.data.previous ? { previous: ts.data.previous.map((p) => fmtBucket(p.t, ts.data.interval)) } : undefined}
                  series={[
                    ...(ts.data.previous
                      ? [{ id: "previous", label: "Previous period", color: "var(--prev)", muted: true, values: ts.data.previous.map((p) => p[metric]) }]
                      : []),
                    { id: "current", label: "This period", color: "var(--series-1)", values: ts.data.current.map((p) => p[metric]) },
                  ]}
                  format={metricOf(metric).fmt}
                  markers={releaseMarkers(releases.data, buckets)}
                  table={{
                    caption: `${metricOf(metric).label} per ${ts.data.interval}, ${ts.data.previous ? "this period and the previous one" : "this period"}, oldest first`,
                    rowHeader: fmtBucketHeader(ts.data.interval),
                  }}
                />
                <ReleaseList appId={appId} releases={releases.data} />
                {ts.data.previous && (
                  <div className="px-3">
                    <ChartLegend
                      inline
                      shape="line"
                      series={[
                        { id: "current", label: "This period", color: "var(--series-1)" },
                        { id: "previous", label: "Previous period", color: "var(--prev)" },
                      ]}
                    />
                  </div>
                )}
              </>
            ) : (
              <Skeleton className="mx-2 h-[240px]" />
            )}
          </SummaryTiles>
        </Card>

        <div className="grid gap-4 @4xl:grid-cols-2" data-tour="overview-breakdowns">
          <BreakdownCard appId={appId} title="Pages" tabs={["path", "entry_path", "exit_path"]} labels={{ path: "Top pages", entry_path: "Entry", exit_path: "Exit" }} />
          <BreakdownCard appId={appId} title="Acquisition" tabs={["referrer_domain", "utm_source", "utm_medium", "utm_campaign"]} labels={{ referrer_domain: "Referrers", utm_source: "Source", utm_medium: "Medium", utm_campaign: "Campaign" }} />
          <BreakdownCard appId={appId} title="Locations" tabs={["country"]} />
          <BreakdownCard appId={appId} title="Technology" tabs={["device_type", "browser", "os", "os_version", "app_version", "a11y"]} labels={{ device_type: "Devices", browser: "Browsers", os: "OS", os_version: "OS versions", app_version: "Versions", a11y: "Accessibility" }} />
          <div className="@4xl:col-span-2">
            <BreakdownCard appId={appId} title="Events" tabs={["event"]} subtitle="Custom and automatic events. Mark events as goals to track conversions." valueLabel="Visitors" />
          </div>
          <div className="@4xl:col-span-2">
            <PropertyCard appId={appId} />
          </div>
        </div>
      </div>
    </>
  );
}

const FILTER_FOR: Partial<Record<Dimension, FilterField>> = { entry_path: "path", exit_path: "path" };

function BreakdownCard({
  appId,
  title,
  subtitle,
  tabs,
  labels,
  valueLabel,
}: {
  appId: string;
  title: string;
  subtitle?: string;
  tabs: Dimension[];
  labels?: Partial<Record<Dimension, string>>;
  valueLabel?: string;
}) {
  const [tab, setTab] = useState<Dimension>(tabs[0]!);
  const panelId = useId();
  const q = useBreakdown(appId, tab, 10);
  const goals = useGoals(appId);
  const params = useStatsParams();
  const hasGoals = (goals.data?.length ?? 0) > 0;

  const label = (key: string) => {
    if (tab === "country") return <>{flag(key)} {countryName(key)}</>;
    if (tab === "a11y") return A11Y_LABEL[key] ?? key;
    if (tab === "referrer_domain")
      return (
        <span className="flex items-center gap-2">
          <img src={`https://icons.duckduckgo.com/ip3/${key}.ico`} alt="" width={14} height={14} className="rounded-sm" loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} />
          {key}
        </span>
      );
    return key;
  };

  return (
    <Card className="min-w-0">
      <CardHeader
        title={title}
        subtitle={subtitle}
        actions={
          tabs.length > 1 && (
            <TabBar
              aria-label={`${title} breakdown`}
              panelId={panelId}
              items={tabs.map((t) => ({ value: t, label: labels?.[t] ?? DIMENSION_LABEL[t]! }))}
              value={tab}
              onChange={(v) => setTab(v as Dimension)}
            />
          )
        }
      />
      <div
        className="min-h-[200px] pb-3"
        {...(tabs.length > 1 ? { id: panelId, role: "tabpanel", "aria-label": labels?.[tab] ?? DIMENSION_LABEL[tab] ?? tab } : {})}
      >
        {q.isError ? (
          <ErrorState error={q.error} />
        ) : q.isPending ? (
          <div className="space-y-2 px-4">
            {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-7" />)}
          </div>
        ) : q.data?.length ? (
          <BarList
            aria-label={DIMENSION_LABEL[tab] ?? tab}
            valueLabel={valueLabel}
            rows={q.data.map((r) => ({
              key: r.key,
              label: label(r.key),
              text: tab === "country" ? countryName(r.key) : tab === "a11y" ? (A11Y_LABEL[r.key] ?? r.key) : r.key,
              value: r.visitors,
              secondary: hasGoals ? `${fmtPercent(r.visitors ? r.conversions / r.visitors : 0)} CR` : undefined,
            }))}
            onSelect={(key) => params.addFilter({ field: FILTER_FOR[tab] ?? (tab as FilterField), op: "eq", value: key })}
          />
        ) : (
          <Empty title="No data for this period" />
        )}
      </div>
    </Card>
  );
}

/**
 * Breakdown by a custom property the app sends with its events (plan, variant…). Pick the key; select
 * a value to filter every report by it. Past the top values, the rest count together as "Other".
 */
function PropertyCard({ appId }: { appId: string }) {
  const keys = usePropertyKeys(appId);
  const [picked, setPicked] = useState<string>();
  const key = picked ?? keys.data?.[0]?.key;
  const q = useBreakdown(appId, "property", 10, key);
  const goals = useGoals(appId);
  const params = useStatsParams();
  const hasGoals = (goals.data?.length ?? 0) > 0;
  const items = (keys.data ?? []).map((k) => ({ value: k.key, label: k.key }));
  // A key picked under another date range stays chosen even if this one hasn't seen it.
  if (key && !items.some((i) => i.value === key)) items.unshift({ value: key, label: key });

  return (
    <Card className="min-w-0">
      <CardHeader
        title="Custom properties"
        subtitle="Details your app sends with its events, like plan or variant. Select a value to filter by it."
        actions={items.length > 0 && <SelectField aria-label="Custom property" items={items} value={key} onChange={setPicked} className="w-48" />}
      />
      <div className="min-h-[200px] pb-3">
        {keys.isError || q.isError ? (
          <ErrorState error={keys.error ?? q.error} />
        ) : keys.isPending || (key && q.isPending) ? (
          <div className="space-y-2 px-4">
            {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-7" />)}
          </div>
        ) : !key ? (
          <Empty title="No custom properties in this period">
            Send them with your events, like <InlineCode>{'track("upgrade", { plan: "pro" })'}</InlineCode>. Personal details such as email addresses are never
            listed.
          </Empty>
        ) : q.data?.length ? (
          <BarList
            aria-label={`Custom property ${key}`}
            rows={q.data.map((r) => ({
              key: r.other ? "\u0000other" : r.key,
              label: r.other ? <span className="text-ink-2">Other</span> : r.key,
              text: r.other ? "Other values" : r.key,
              value: r.visitors,
              selectable: !r.other,
              secondary: hasGoals ? `${fmtPercent(r.visitors ? r.conversions / r.visitors : 0)} CR` : undefined,
            }))}
            onSelect={(value) => params.addFilter({ field: "property", key, op: "eq", value })}
          />
        ) : (
          <Empty title={`No events with ${key} in this period`} />
        )}
      </div>
    </Card>
  );
}
